import * as React from 'react';
import {
  Box,
  StackLayout,
  UtilityText,
  ProgressBar,
  HeadingText,
  ParagraphText,
  Popover,
  Button,
  GDSHeadingLevel,
  GDSHeadingSize,
  focusVisibleOverrideOuter,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useUniqueId } from '@leagueplatform/web-common';
import type {
  Icon,
  TooltipData,
} from '../../masonry/renderers/plan-usage-widget/plan-usage-widget.types';
import { overrideParagraphSpacing } from '../../masonry/renderers/plan-usage-widget/constants/genesis-override-styles.constants';

interface AccumulatorCardProps {
  eyebrow?: string;
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  headingSize?: GDSHeadingSize;
  tooltip?: TooltipData;
  amountSpent?: string;
  totalAmount?: string;
  progressValue?: string;
  amountLeft?: string;
  onTooltipClick?: (tooltipId: string) => void;
}

interface AccumulatorCardTooltipProps {
  label: string;
  labelIcon?: Icon;
  handleTooltipClick: () => void;
  title: string;
  description: string;
}

function AccumulatorCardTooltip({
  label,
  labelIcon,
  handleTooltipClick,
  title,
  description,
}: AccumulatorCardTooltipProps) {
  const icon = labelIcon?.icon || 'interfaceQuestionCircle';
  const { formatMessage } = useIntl();
  return (
    <Popover.Root>
      <Popover.Trigger
        size="small"
        priority="tertiary"
        onClick={handleTooltipClick}
        label={label}
        icon={icon}
        hideLabel
        quiet
        css={focusVisibleOverrideOuter}
      />
      <Popover.Content
        align="center"
        side="top"
        css={{ maxWidth: '300px' }}
        headingBar={
          <Box
            css={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '$quarter',
              marginBlockEnd: '$quarter',
            }}
          >
            {title && (
              <HeadingText size="sm" level="3">
                {title}
              </HeadingText>
            )}
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Popover.Close asChild>
                <Button
                  type="button"
                  icon="tinyClose"
                  hideLabel
                  quiet
                  priority="secondary"
                  size="small"
                  className="GDS-popover-close"
                  css={{
                    color: '$interactiveActionSubdued',
                    svg: {
                      width: '$threeQuarters',
                      height: '$threeQuarters',
                    },
                    position: 'relative',
                    top: '-$quarter',
                    ...focusVisibleOverrideOuter,
                  }}
                >
                  {formatMessage({ id: 'CLOSE' })}
                </Button>
              </Popover.Close>
            </Box>
          </Box>
        }
      >
        <StackLayout spacing="$two">
          {description && (
            <ParagraphText size="sm">{description}</ParagraphText>
          )}
        </StackLayout>
      </Popover.Content>
    </Popover.Root>
  );
}

const getProgressValue = (progressValue: string | undefined) => {
  if (!progressValue) {
    return 0;
  }
  const parsed = parseInt(progressValue, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export function AccumulatorCard({
  eyebrow,
  heading,
  headingLevel = '3',
  headingSize = 'sm',
  tooltip,
  amountSpent,
  totalAmount,
  progressValue,
  amountLeft,
  onTooltipClick: onTooltipClickCallback,
}: AccumulatorCardProps) {
  const progressValueNum = React.useMemo(
    () => getProgressValue(progressValue),
    [progressValue],
  );
  const headingId = useUniqueId({ prefix: 'accumulator-card-heading' });

  const handleTooltipClick = React.useCallback(() => {
    if (onTooltipClickCallback) {
      onTooltipClickCallback(tooltip?.id || '');
    }
  }, [onTooltipClickCallback, tooltip?.id]);

  return (
    <StackLayout
      spacing="$quarter"
      css={{
        backgroundColor: '$surfaceBackgroundPrimary',
        padding: '$oneAndHalf',
        borderRadius: '$card',
      }}
    >
      {eyebrow && (
        <UtilityText as="p" size="eyebrow" css={{ textTransform: 'uppercase' }}>
          {eyebrow}
        </UtilityText>
      )}
      <StackLayout spacing="$threeQuarters" css={{ width: '100%' }}>
        <StackLayout orientation="horizontal" verticalAlignment="center">
          {heading && (
            <HeadingText id={headingId} level={headingLevel} size={headingSize}>
              {heading}
            </HeadingText>
          )}
          {tooltip && (
            <AccumulatorCardTooltip
              label={tooltip.label || ''}
              labelIcon={tooltip.icon}
              handleTooltipClick={handleTooltipClick}
              title={tooltip.title || ''}
              description={tooltip.description || ''}
            />
          )}
        </StackLayout>
        <ParagraphText
          size="sm"
          css={{ ...overrideParagraphSpacing, typography: '$bodyTwo' }}
        >
          <UtilityText
            size="sm"
            as="strong"
            css={{ typography: '$subtitleOne' }}
          >
            {amountSpent}
          </UtilityText>{' '}
          {totalAmount}
        </ParagraphText>
        <Box css={{ width: '100%' }}>
          <ProgressBar
            stopIndicator={false}
            fillProgressValue={progressValueNum}
            accessibilityLabel=""
            aria-labelledby={heading ? headingId : undefined}
          />
        </Box>
        {amountLeft && (
          <UtilityText
            css={{
              typography: '$subtitleTwo',
              alignSelf: 'flex-end',
            }}
          >
            {amountLeft}
          </UtilityText>
        )}
      </StackLayout>
    </StackLayout>
  );
}
